import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
interface SearchableLookupProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  label: string;
  icon?: React.ElementType;
  isChanged?: boolean;
}
export function SearchableLookup({
  value,
  options,
  onChange,
  placeholder = 'Search...',
  label,
  icon: Icon,
  isChanged = false
}: SearchableLookupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, []);
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target) && dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  const filtered = options.filter(opt => opt.toLowerCase().includes(query.toLowerCase()));
  const handleSelect = (opt: string) => {
    onChange(opt);
    setIsOpen(false);
    setQuery('');
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };
  const labelClass = 'flex items-center text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1';
  const baseInputClass = `block w-full py-1.5 px-2.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:border-[#D44444]`;
  const activeClass = isChanged ? 'border-[#D44444] bg-red-50 text-[#D44444] font-medium' : 'border-gray-300 bg-white text-gray-900';
  return <div className="flex flex-col relative" ref={containerRef} data-id="element-219">
      <label className={labelClass} data-id="element-220">
        {Icon && <Icon className="w-3 h-3 mr-1" data-id="element-221" />}
        {label}
      </label>

      {/* Trigger / display */}
      {!isOpen ? <button type="button" onClick={() => setIsOpen(true)} className={`${baseInputClass} ${activeClass} text-left truncate flex items-center justify-between`} data-id="element-222">
          <span className={`truncate ${!value ? 'text-gray-400' : ''}`} data-id="element-223">
            {value || placeholder}
          </span>
          {value ? <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 flex-shrink-0 ml-1" onClick={handleClear} data-id="element-224" /> : <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-1" data-id="element-225" />}
        </button> : <div className="relative" data-id="element-226">
          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none" data-id="element-227">
            <Search className="w-3.5 h-3.5 text-gray-400" data-id="element-228" />
          </div>
          <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
        if (e.key === 'Escape') {
          setIsOpen(false);
          setQuery('');
        }
        if (e.key === 'Enter' && filtered.length === 1) {
          handleSelect(filtered[0]);
        }
      }} placeholder={placeholder} className={`${baseInputClass} border-[#D44444] ring-1 ring-[#D44444] pl-7 pr-2.5`} data-id="element-229" />
        </div>}

      {/* Dropdown rendered via portal to escape overflow:hidden parents */}
      {isOpen && createPortal(<div ref={dropdownRef} style={{
      position: 'absolute',
      top: dropdownPos.top,
      left: dropdownPos.left,
      width: dropdownPos.width,
      zIndex: 9999
    }} className="bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto" data-id="element-230">
            {filtered.length === 0 ? <div className="px-3 py-2 text-xs text-gray-400 italic" data-id="element-231">
                No results found
              </div> : filtered.map(opt => <button key={opt} type="button" onClick={() => handleSelect(opt)} className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors flex items-center ${opt === value ? 'bg-red-50 text-[#D44444] font-medium' : 'text-gray-700'}`} data-id="element-232">
                  {opt}
                </button>)}
          </div>, document.body)}
    </div>;
}